import { createColumnHelper } from "@tanstack/react-table";
import { EmptyValue } from "@/components/ui/record-table/empty-value";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { listingHref } from "@shared/routing/listing-href";
import { cn } from "@/lib/utils";
import { flexibleWidth } from "@/components/ui/record-table/grid/grid-widths";
import { formatDimensionsCompact } from "@/lib/specs/dimensions";
import { formatSpecValue } from "@/lib/specs/labels";
import {
  MAIN_PRODUCT_TYPE_SLUGS,
  isProductType,
  type FieldDefinition,
} from "@/lib/generated/product-types";
import { getFieldsForType } from "@/lib/generated/product-type-fields";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import {
  ListedAtCell,
  ProductTypeCell,
  ProductImageCell,
  LocationCell,
  ZipCell,
  PriceCell,
  ConditionBadge,
} from "./column-cells";

const columnHelper = createColumnHelper<RecordGridFeatures, BrowseListing>();

export type { FilterType } from "@/components/ui/record-table/column-types";
import type {
  FilterType,
  ColumnMeta as BaseColumnMeta,
} from "@/components/ui/record-table/column-types";

export interface ColumnMeta extends BaseColumnMeta {
  isSpec?: boolean;
  productTypes?: string[];
}


const listedAt = (value: unknown): number =>
  typeof value === "number" ? value : 0;

const slugOf = (value: unknown): string =>
  typeof value === "string" ? value : "";

const numberOrNull = (value: unknown): number | null =>
  typeof value === "number" ? value : null;

const stringOrUndefined = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;



type ProductDimensions = NonNullable<BrowseListing["dimensions"]>;

const DIMENSION_KEYS = [
  "length",
  "width",
  "height",
  "capacity",
  "unit",
  "capacityUnit",
] as const satisfies readonly (keyof ProductDimensions)[];

function isDimensionKey(key: string): key is keyof ProductDimensions {
  return DIMENSION_KEYS.some((dimensionKey) => dimensionKey === key);
}

function getSpecValue(product: BrowseListing, key: string): unknown {
  if (isDimensionKey(key)) {
    return product.dimensions ? (product.dimensions[key] ?? null) : null;
  }
  if (!product.specs) return null;
  return product.specs[key] ?? null;
}

const BASE_COLUMNS: RecordColumnDef<BrowseListing, unknown>[] = [
  columnHelper.accessor(
    (row): unknown => row.publishedAt ?? row._creationTime,
    {
      id: "listedAt",
      header: "Listed",
      size: 70,
      enableSorting: true,
      meta: { filterType: "none" } satisfies ColumnMeta,
      cell: ({ getValue }) => <ListedAtCell timestamp={listedAt(getValue())} />,
    },
  ),

  columnHelper.accessor((row): unknown => row.productTypeSlug, {
    id: "productType",
    header: "Type",
    size: 110,
    enableSorting: true,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ getValue }) => <ProductTypeCell slug={slugOf(getValue())} />,
  }),

  columnHelper.accessor((row): unknown => row.primaryImageUrl, {
    id: "image",
    header: "",
    size: 72,
    enableSorting: false,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ row }) => <ProductImageCell product={row.original} />,
  }),

  columnHelper.accessor(
    (row): unknown => formatDimensionsCompact(row.dimensions),
    {
      id: "dimensions",
      header: "Size",
      size: 80,
      enableSorting: false,
      meta: { filterType: "none" } satisfies ColumnMeta,
      cell: ({ getValue }) => {
        const value = stringOrUndefined(getValue());
        if (!value) return <EmptyValue />;
        return (
          <span className="text-muted-foreground tabular-nums">
            {value}
          </span>
        );
      },
    },
  ),

  columnHelper.accessor(
    (row): unknown => {
      const city = row.cityName;
      const state = row.stateAbbr;
      if (city && state) return `${city}, ${state}`;
      return city || state || null;
    },
    {
      id: "location",
      header: "Location",
      size: 120,
      enableSorting: true,
      meta: flexibleWidth({ filterType: "none" } satisfies ColumnMeta),
      cell: ({ row }) => (
        <LocationCell
          city={row.original.cityName}
          state={row.original.stateAbbr}
        />
      ),
    },
  ),

  columnHelper.accessor((row): unknown => row.originZip ?? null, {
    id: "zip",
    header: "ZIP",
    size: 80,
    enableSorting: false,
    meta: { filterType: "none" } satisfies ColumnMeta,
    cell: ({ row }) => <ZipCell zip={row.original.originZip} />,
  }),

  columnHelper.accessor((row): unknown => row.buyerUnitPriceCents, {
    id: "unitPrice",
    header: "Price",
    size: 80,
    enableSorting: true,
    meta: { filterType: "range", align: "right" } satisfies ColumnMeta,
    cell: ({ getValue }) => <PriceCell cents={numberOrNull(getValue())} />,
  }),
];

export function getBaseColumns(
  requestedQuantity?: number,
  onSelect?: (product: BrowseListing) => void,
): RecordColumnDef<BrowseListing, unknown>[] {
  return [
    ...BASE_COLUMNS.slice(0, 3),
    columnHelper.accessor((row): unknown => row.title, {
      id: "title",
      header: "Product",
      enableHiding: false,
      size: 240,
      enableSorting: true,
      meta: flexibleWidth({ filterType: "text" } satisfies ColumnMeta),
      cell: ({ row }) => (
        <HoverPrefetchLink
          href={listingHref(row.original, requestedQuantity)}
          onClick={() => onSelect?.(row.original)}
          className="block truncate text-sm font-semibold text-foreground hover:text-action cursor-pointer transition-colors"
        >
          {row.original.title}
        </HoverPrefetchLink>
      ),
    }),
    ...BASE_COLUMNS.slice(3),
  ];
}

function sentenceCaseHeader(label: string): string {
  return label
    .split(" ")
    .map((word, index) => {
      if (index === 0) return word;
      const isAcronym = word.length > 1 && word === word.toUpperCase();
      return isAcronym || /\d/.test(word) ? word : word.toLowerCase();
    })
    .join(" ");
}

function fieldToColumn(
  field: FieldDefinition,
): RecordColumnDef<BrowseListing, unknown> {
  const { name, type, label, options } = field;

  const filterType: FilterType =
    type === "select"
      ? "select"
      : type === "boolean"
        ? "boolean"
        : type === "number"
          ? "range"
          : "text";

  const filterOptions = options?.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));

  return columnHelper.accessor(
    (row): unknown => {
      if (type === "dimensions") {
        return formatDimensionsCompact(row.dimensions);
      }
      return getSpecValue(row, name);
    },
    {
      id: `spec_${name}`,
      header: sentenceCaseHeader(label),
      size: type === "boolean" ? 50 : 90,
      enableSorting: type === "select" || type === "boolean",
      meta: { filterType, filterOptions, isSpec: true } satisfies ColumnMeta,
      cell: ({ getValue, row }) => (
        <SpecCell
          value={getValue()}
          type={type}
          name={name}
          productTypeSlug={row.original.productTypeSlug}
        />
      ),
    },
  );
}

function SpecCell({
  value,
  type,
  name,
  productTypeSlug,
}: {
  value: unknown;
  type: string;
  name: string;
  productTypeSlug?: string;
}) {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  if (type === "boolean") {
    return (
      <span
        className={cn(
          "text-xs font-medium",
          value ? "text-success-strong" : "text-muted-foreground",
        )}
      >
        {value ? "Yes" : "No"}
      </span>
    );
  }

  if (type === "select" && name === "condition") {
    return (
      <ConditionBadge
        productTypeSlug={productTypeSlug}
        conditionValue={String(value)}
      />
    );
  }

  const formatted = formatSpecValue(value);
  return (
    <span className="text-xs text-muted-foreground truncate block max-w-[90px]">
      {formatted}
    </span>
  );
}

export function getColumnsForProductType(
  productTypeSlug: string | null,
  requestedQuantity?: number,
  onSelect?: (product: BrowseListing) => void,
): RecordColumnDef<BrowseListing, unknown>[] {
  const baseColumns = getBaseColumns(requestedQuantity, onSelect);

  if (!productTypeSlug) {
    return baseColumns;
  }

  if (!isProductType(productTypeSlug)) {
    return baseColumns;
  }

  const fields = getFieldsForType(productTypeSlug);
  if (fields.length === 0) {
    return baseColumns;
  }

  const specColumns = [...fields].map(fieldToColumn);
  return [...baseColumns.slice(0, 3), ...specColumns, ...baseColumns.slice(3)];
}

export function getAllProductTypes(): string[] {
  return [...MAIN_PRODUCT_TYPE_SLUGS];
}
